import { Box, Button, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useRef, useState } from 'react';
import ImageCropDialog from '../../components/ImageCropDialog';
import { brandAssetUrl, brandConfig } from '../../branding/brandConfig';
import tenantService from './tenantService';

const ACCEPT = 'image/png,image/jpeg,image/webp,image/svg+xml';
const MAX_BYTES = 10 * 1024 * 1024;

async function fileToBase64(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function uploadErrorMessage(error: any): string {
  const data = error?.response?.data;
  if (Array.isArray(data?.errorMessages) && data.errorMessages.length) {
    return data.errorMessages.join(', ');
  }
  if (typeof data === 'string' && data.trim()) {
    return data;
  }
  if (data?.title) {
    return data.title;
  }
  if (error?.response?.status === 404) {
    return 'A logó feltöltő endpoint nem elérhető (API újraindítás kellhet).';
  }
  return error?.message || 'Logó feltöltés sikertelen.';
}

type TenantBrandingLogoFieldProps = {
  tenantId: string;
  logoUrl?: string | null;
  logoPreviewUrl?: string | null;
  readonly?: boolean;
  onUploaded: (branding: any) => void;
  onCleared: () => void;
};

const TenantBrandingLogoField = ({
  tenantId,
  logoUrl,
  logoPreviewUrl,
  readonly = false,
  onUploaded,
  onCleared,
}: TenantBrandingLogoFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { enqueueSnackbar } = useSnackbar();
  const [cropSource, setCropSource] = useState<File | null>(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const hasCustomLogo = !!logoUrl;
  const previewSrc =
    logoPreviewUrl ||
    (hasCustomLogo ? logoUrl : brandAssetUrl(brandConfig.assets.logoPng));

  const handlePick = (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      enqueueSnackbar('Csak képfájl tölthető fel.', { variant: 'error' });
      return;
    }
    if (file.size > MAX_BYTES) {
      enqueueSnackbar('A kép legfeljebb 10 MB lehet.', { variant: 'error' });
      return;
    }
    // SVG: skip crop dialog
    if (file.type === 'image/svg+xml') {
      void uploadFile(file);
      return;
    }
    setCropSource(file);
    setCropOpen(true);
  };

  const uploadFile = async (file: File) => {
    if (!tenantId) {
      enqueueSnackbar('Nincs bérlő azonosító a feltöltéshez.', {
        variant: 'error',
      });
      throw new Error('Missing tenantId');
    }

    setUploading(true);
    try {
      const response = await tenantService.uploadBrandingLogo(tenantId, {
        fileName: file.name,
        contentBase64: await fileToBase64(file),
      });
      if (response.hasError) {
        throw Object.assign(
          new Error((response.errorMessages ?? []).join(', ') || 'Hiba'),
          { response: { data: response } }
        );
      }
      const result = response.result ?? {};
      const localPreview = URL.createObjectURL(file);
      onUploaded({
        ...result,
        logoUrl: result.logoUrl ?? result.LogoUrl ?? null,
        logoPreviewUrl:
          result.logoPreviewUrl ?? result.LogoPreviewUrl ?? localPreview,
      });
      enqueueSnackbar('Logó feltöltve.', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(uploadErrorMessage(error), { variant: 'error' });
      throw error;
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="subtitle1" gutterBottom>
        Logó
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {hasCustomLogo
          ? 'Bérlő-specifikus logó'
          : `Alapértelmezett: ${brandConfig.productName} logó`}
      </Typography>
      {previewSrc && (
        <Box
          component="img"
          src={previewSrc}
          alt={brandConfig.logoAlt}
          sx={{
            display: 'block',
            maxWidth: 200,
            maxHeight: 120,
            mb: 1,
            objectFit: 'contain',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            p: 1,
            bgcolor: 'background.paper',
          }}
        />
      )}
      {!readonly && (
        <>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            hidden
            onChange={(e) => {
              handlePick(e.target.files?.[0] ?? null);
              e.target.value = '';
            }}
          />
          <Button
            variant="outlined"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            sx={{ mr: 1 }}
          >
            {uploading
              ? 'Feltöltés…'
              : hasCustomLogo
                ? 'Logó cseréje'
                : 'Logó feltöltése'}
          </Button>
          {hasCustomLogo && (
            <Button
              variant="text"
              color="secondary"
              disabled={uploading}
              onClick={onCleared}
            >
              Eltávolítás
            </Button>
          )}
        </>
      )}
      <ImageCropDialog
        open={cropOpen}
        sourceFile={cropSource}
        aspect={16 / 9}
        title="Logó"
        onClose={() => {
          setCropOpen(false);
          setCropSource(null);
        }}
        onConfirm={uploadFile}
      />
    </Box>
  );
};

export default TenantBrandingLogoField;
